import React, { useState, useEffect } from 'react';
import './CaseDeleteModal.css';
import { useCasesApi } from '../hooks/useCasesApi';
import EditCase from './EditCase';
import CaseDetails from './CaseDetails';
import NewCaseModal from './NewCaseModal';
import CaseDeleteModal from './CaseDeleteModal';
import { Case } from '../hooks/useCasesApi';
import { apiClient } from '../services/api';
import {
  FiFolder,
  FiClock,
  FiCheckCircle,
  FiUsers,
  FiSearch,
  FiRefreshCw,
  FiPlus,
  FiEye,
  FiEdit3,
  FiTrash2,
  FiUser,
  FiX,
  FiAlertTriangle
} from 'react-icons/fi';
import './CaseDashboard.css';

export default function CaseDashboard() {
  const { cases, loading, createCase, forceRefresh } = useCasesApi();
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCase, setEditingCase] = useState<Case | null>(null);
  const [viewingCase, setViewingCase] = useState<Case | null>(null);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render key
  const [deletingCase, setDeletingCase] = useState<Case | null>(null);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [caseContent, setCaseContent] = useState<{[key: string]: {documents: number, forms: number}}>({});

  // Force a re-render when cases change
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [cases]);

  // Check case content (documents and forms) when cases change - optimized
  useEffect(() => {
    const checkCaseContent = async () => {
      const contentMap: {[key: string]: {documents: number, forms: number}} = {};
      
      // Add a timeout to prevent hanging
      const timeoutId = setTimeout(() => {
        console.log('⏰ Content check timeout - using cached values');
        setCaseContent(contentMap);
      }, 10000); // 10 second timeout
      
      for (const case_ of cases) {
        try {
          // Use cached data by default (no forceRefresh) to reduce database load
          const [documents, forms] = await Promise.all([
            apiClient.getDocumentsForCase(case_.id),
            apiClient.getUserForms(case_.id, false) // Don't force refresh
          ]);
          
          const docCount = documents?.length || 0;
          const formCount = forms?.length || 0;
          
          contentMap[case_.id] = {
            documents: docCount,
            forms: formCount
          };
          
          // Reduce logging frequency
          if (process.env.NODE_ENV === 'development') {
            console.log(`📊 Case ${case_.id} content: ${docCount} documents, ${formCount} forms`);
          }
        } catch (error) {
          console.error(`❌ Error checking content for case ${case_.id}:`, error);
          // If there's an error, assume the case is empty and can be deleted
          contentMap[case_.id] = { documents: 0, forms: 0 };
        }
      }
      
      clearTimeout(timeoutId);
      setCaseContent(contentMap);
    };
    
    if (cases.length > 0) {
      checkCaseContent();
    }
  }, [cases]);

  // Listen for global refresh requests (from assistant)
  useEffect(() => {
    const handler = async () => {
      try { await forceRefresh(); } catch (e) { console.error('cases:refresh error', e); }
    };
    window.addEventListener('cases:refresh', handler);
    return () => window.removeEventListener('cases:refresh', handler);
  }, [forceRefresh]);

  const filteredCases = cases.filter(case_ => 
    case_.caseId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    case_.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    case_.caseType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    totalCases: cases.length,
    pending: cases.filter(c => c.status === 'waiting_for_intake').length,
    completed: cases.filter(c => c.status === 'complete' || c.status === 'rejected').length,
    inProgress: cases.filter(c => c.status === 'in_progress').length,
    activeClients: new Set(cases.map(c => c.clientEmail)).size
  };

  const handleDeleteCase = async (caseId: string) => {
    try {
      await apiClient.deleteCase(caseId);
      await forceRefresh();
      setDeletingCase(null);
    } catch (error) {
      console.error('Error deleting case:', error);
      alert('Failed to delete case. Please try again.');
    }
  };

  const canDeleteCase = (caseId: string) => {
    const content = caseContent[caseId];
    
    // If content hasn't been checked yet, allow deletion (assume empty)
    if (!content) {
      console.log(`🗑️ Delete check for case ${caseId}: Content not checked yet, allowing deletion`);
      return true;
    }
    
    const canDelete = content.documents === 0 && content.forms === 0;
    
    console.log(`🗑️ Delete check for case ${caseId}:`, {
      content,
      canDelete,
      documents: content.documents,
      forms: content.forms
    });
    
    return canDelete;
  };

  // if (loading) {
  //   return <div className="loading">Loading cases...</div>;
  // }

  const statusStyles: Record<string, { bg: string; color: string; label: string }> = {
    waiting_for_intake: { bg: "rgba(16, 185, 129, 0.1)", color: "#10b981", label: "Waiting for intake" },
    form_submitted: { bg: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6", label: "Form Submitted" },
    pending_review: { bg: "rgba(59, 130, 246, 0.1)", color: "#3b82f6", label: "Pending Review" },
    in_progress: { bg: "rgba(234, 179, 8, 0.1)", color: "#ea4808ff", label: "In Progress" },
    auto_fill: { bg: "rgba(16, 185, 129, 0.1)", color: "#10aeb9ff", label: "Auto Filled" },
    complete: { bg: "rgba(16, 185, 129, 0.1)", color: "#10b981", label: "Complete" },
  };

  const handleStatusUpdate = async (case_: Case, newStatus: string) => {
    try {
      const updated = await apiClient.updateCase(case_.id, {
        ...case_,
        status: newStatus,
      });
      await forceRefresh();
      console.log("✅ Status updated:", updated.status);
    } catch (err) {
      console.error("❌ Failed to update status:", err);
    }
  };

  return (
    <div className="case-dashboard">
      <div className="dashboard-header" style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid transparent',
        padding: '20px',
        borderRadius: '16px',
        marginBottom: '24px'
      }}>
        <h2 style={{
          fontSize: '28px',
          margin: '0',
          color: '#000000',
          fontWeight: '700'
        }}>Case Dashboard</h2>
      </div>
      
      {/* Unified Dashboard Container */}
      <div className="dashboard-container" style={{
        background: 'rgba(255, 255, 255, 0.02)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        {/* Summary Cards */}
        <div className="stats-bar" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <div className="stat-item">
            <div className="stat-icon" style={{
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              color: 'white',
              padding: '12px',
              borderRadius: '12px',
              fontSize: '20px'
            }}>{React.createElement(FiFolder as any)}</div>
            <div className="stat-content">
              <div className="stat-number" style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#000000',
                marginBottom: '4px'
              }}>{stats.totalCases}</div>
              <div className="stat-label" style={{
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500'
              }}>Total Cases</div>
            </div>
          </div>
          <div className="stat-item" style={{
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            transition: 'all 0.3s ease'
          }}>
            <div className="stat-icon" style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: 'white',
              padding: '12px',
              borderRadius: '12px',
              fontSize: '20px'
            }}>{React.createElement(FiClock as any)}</div>
            <div className="stat-content">
              <div className="stat-number" style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#000000',
                marginBottom: '4px'
              }}>{stats.inProgress}</div>
              <div className="stat-label" style={{
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500'
              }}>In Progress</div>
            </div>
          </div>
          <div className="stat-item" style={{
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            transition: 'all 0.3s ease'
          }}>
            <div className="stat-icon" style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: 'white',
              padding: '12px',
              borderRadius: '12px',
              fontSize: '20px'
            }}>{React.createElement(FiCheckCircle as any)}</div>
            <div className="stat-content">
              <div className="stat-number" style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#000000',
                marginBottom: '4px'
              }}>{stats.completed}</div>
              <div className="stat-label" style={{
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500'
              }}>Completed</div>
            </div>
          </div>
          <div className="stat-item" style={{
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            transition: 'all 0.3s ease'
          }}>
            <div className="stat-icon" style={{
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              color: 'white',
              padding: '12px',
              borderRadius: '12px',
              fontSize: '20px'
            }}>{React.createElement(FiUsers as any)}</div>
            <div className="stat-content">
              <div className="stat-number" style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#000000',
                marginBottom: '4px'
              }}>{stats.activeClients}</div>
              <div className="stat-label" style={{
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500'
              }}>Active Clients</div>
            </div>
          </div>
        </div>

        {/* Recent Cases Section */}
        <div className="recent-cases" style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          padding: '24px'
        }}>
        <div className="section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#000000',
            margin: '0'
          }}>Recent Cases</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div className="search-bar" style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <input
                type="text"
                placeholder="Search cases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(229, 231, 235, 0.4)',
                  borderRadius: '12px',
                  padding: '10px 40px 10px 16px',
                  fontSize: '14px',
                  color: '#000000',
                  outline: 'none',
                  width: '200px',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              />
              <span className="search-icon" style={{
                position: 'absolute',
                right: '12px',
                color: '#6b7280',
                fontSize: '16px'
              }}>{React.createElement(FiSearch as any)}</span>
            </div>
            <button 
              className="dashboard-action-btn"
              onClick={async () => {
                setRefreshKey(prev => prev + 1);
                await forceRefresh();
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#000000',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {React.createElement(FiRefreshCw as any)} Refresh
            </button>
            <button 
              className="dashboard-action-btn"
              onClick={() => setShowNewCaseModal(true)}
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                borderRadius: '12px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {React.createElement(FiPlus as any)} New Case
            </button>
          </div>
        </div>

        {cases.length === 0 ? (
          <div className="empty-state" style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}>
            <div className="empty-icon" style={{
              fontSize: '48px',
              color: '#6b7280',
              marginBottom: '16px'
            }}>{React.createElement(FiFolder as any)}</div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#000000',
              marginBottom: '8px'
            }}>No cases yet</h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '24px'
            }}>Get started by creating your first case</p>
            <button className="new-case-btn" onClick={() => window.location.hash = '#newCase'} style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {React.createElement(FiPlus as any)} New Case
            </button>
          </div>
        ) : (
          <div className="cases-table" style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            overflow: 'hidden'
          }}>
            <table key={refreshKey} style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <th style={{
                    padding: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textAlign: 'left',
                    letterSpacing: '0.5px'
                  }}>CASE ID</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textAlign: 'left',
                    letterSpacing: '0.5px'
                  }}>CLIENT</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textAlign: 'left',
                    letterSpacing: '0.5px'
                  }}>TYPE</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textAlign: 'left',
                    letterSpacing: '0.5px'
                  }}>STATUS</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textAlign: 'left',
                    letterSpacing: '0.5px'
                  }}>PRIORITY</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textAlign: 'left',
                    letterSpacing: '0.5px'
                  }}>LAST UPDATED</th>
                  <th style={{
                    padding: '16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6b7280',
                    textAlign: 'left',
                    letterSpacing: '0.5px'
                  }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((case_) => (
                  <tr key={case_.id} style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.3s ease'
                  }}>
                    <td style={{ padding: '16px' }}>
                      <button 
                        className="case-id-link"
                        onClick={() => setViewingCase(case_)}
                        title="View case details"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#3b82f6',
                          fontWeight: '500',
                          fontSize: '14px',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        {case_.caseId}
                      </button>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div className="client-info" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span className="client-avatar" style={{
                          background: 'rgba(59, 130, 246, 0.1)',
                          color: '#3b82f6',
                          padding: '8px',
                          borderRadius: '8px',
                          fontSize: '16px'
                        }}>{React.createElement(FiUser as any)}</span>
                        <div>
                          <div className="client-name" style={{
                            fontWeight: '500',
                            color: '#000000',
                            fontSize: '14px',
                            marginBottom: '2px'
                          }}>{case_.clientName}</div>
                          <div className="client-email" style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>{case_.clientEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#000000'
                    }}>{case_.caseType}
                    </td>

                    <td style={{ padding: "16px" }}>
                        <select
                          value={case_.status}
                          onChange={(e) => handleStatusUpdate(case_, e.target.value)}
                          style={{
                            background: statusStyles[case_.status]?.bg || "rgba(229, 231, 235, 0.5)",
                            color: statusStyles[case_.status]?.color || "#374151",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "500",
                            border: "none",
                            outline: "none",
                            cursor: "pointer",
                          }}
                        >
                          {Object.keys(statusStyles).map((status) => (
                            <option key={status} value={status}>
                              {statusStyles[status].label}
                            </option>
                          ))}
                        </select>
                    </td>

                    <td style={{ padding: '16px' }}>
                      <span className={`priority-badge priority-${case_.priority.toLowerCase()}`} style={{
                        background: case_.priority === 'High' ? 'rgba(239, 68, 68, 0.1)' :
                          case_.priority === 'Medium' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                        color: case_.priority === 'High' ? '#ef4444' :
                          case_.priority === 'Medium' ? '#f59e0b' : '#22c55e',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {case_.priority}
                      </span>
                    </td>
                    <td style={{
                      padding: '16px',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>{case_.lastUpdated}</td>
                    <td style={{ padding: '16px' }}>
                      <div className="action-buttons" style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                      }}>
                        <button
                          className="action-btn"
                          title="View"
                          onClick={() => setViewingCase(case_)}
                          style={{
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            fontSize: '14px',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {React.createElement(FiEye as any)}
                        </button>
                        <button
                          className="action-btn"
                          title="Edit"
                          onClick={() => setEditingCase(case_)}
                          style={{
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px',
                            color: '#10b981',
                            cursor: 'pointer',
                            fontSize: '14px',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {React.createElement(FiEdit3 as any)}
                        </button>

                        {canDeleteCase(case_.id) && (
                          <button 
                            className="action-btn" 
                            title="Delete Case"
                            onClick={() => setDeletingCase(case_)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '14px',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {React.createElement(FiTrash2 as any)}
                          </button>
                        )}
                        {/* Debug indicator - shows when delete should be visible but isn't */}
                        {process.env.NODE_ENV === 'development' && !canDeleteCase(case_.id) && (
                          <span 
                            title={`Content check - Documents: ${caseContent[case_.id]?.documents || 'undefined'}, Forms: ${caseContent[case_.id]?.forms || 'undefined'}`}
                            style={{ fontSize: '10px', color: '#ff6b6b', cursor: 'help' }}
                          >
                            {React.createElement(FiAlertTriangle as any)}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>

      {/* Edit Case Modal */}
      {editingCase && (
        <EditCase
          caseData={editingCase}
          onClose={() => setEditingCase(null)}
          onUpdate={async () => {
            // Force multiple re-renders to ensure update
            setRefreshKey(prev => prev + 1);
            
            // Force a database refresh
            await forceRefresh();
            
            // Force another re-render after a short delay
            setTimeout(() => {
              setRefreshKey(prev => prev + 1);
            }, 100);
            
            setEditingCase(null);
          }}
        />
      )}

      {/* Case Details Modal */}
      {viewingCase && (
        <div className="case-details-overlay">
          <CaseDetails
            caseData={viewingCase}
            onClose={() => setViewingCase(null)}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <CaseDeleteModal
        isOpen={!!deletingCase}
        case={deletingCase}
        onConfirm={handleDeleteCase}
        onCancel={() => setDeletingCase(null)}
        documents={deletingCase ? (caseContent[deletingCase.id]?.documents || 0) : 0}
        forms={deletingCase ? (caseContent[deletingCase.id]?.forms || 0) : 0}
      />

      {/* New Case Modal */}
      <NewCaseModal 
        isOpen={showNewCaseModal}
        onClose={() => setShowNewCaseModal(false)}
        onSuccess={() => {
          forceRefresh(); // Refresh the cases list
          setRefreshKey(prev => prev + 1); // Force re-render
        }}
      />
    </div>
  );
} 